import { ArrowRight, Dna, FolderKanban, Microscope, Users } from 'lucide-react';
import {
  alpha,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import logo from '@assets/logo.svg';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@components/PageHeader';

type WorkspaceCardProps = {
  title: string;
  description: string;
  icon: typeof FolderKanban;
  onClick?: () => void;
  state?: string;
};

const WorkspaceCard = ({
  title,
  description,
  icon: Icon,
  onClick,
  state,
}: WorkspaceCardProps) => (
  <Paper
    component={onClick ? 'button' : 'div'}
    type={onClick ? 'button' : undefined}
    onClick={onClick}
    elevation={0}
    sx={(theme) => ({
      width: '100%',
      minHeight: 164,
      p: 2.25,
      borderRadius: 3,
      textAlign: 'left',
      color: 'text.primary',
      cursor: onClick ? 'pointer' : 'default',
      background:
        theme.palette.mode === 'light'
          ? `linear-gradient(145deg, ${
              theme.palette.background.paper
            } 0%, ${alpha(theme.palette.primary.light, 0.82)} 100%)`
          : `linear-gradient(145deg, ${
              theme.palette.background.paper
            } 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
      border: `1px solid ${theme.palette.divider}`,
      transition: theme.transitions.create(
        ['transform', 'box-shadow', 'border-color'],
        {
          duration: theme.transitions.duration.short,
        },
      ),
      '&:hover': onClick
        ? {
            transform: 'translateY(-4px)',
            boxShadow: `0 16px 34px ${alpha(
              theme.palette.primary.dark,
              theme.palette.mode === 'dark' ? 0.28 : 0.14,
            )}`,
            borderColor: 'primary.main',
          }
        : undefined,
    })}
  >
    <Stack height="100%" justifyContent="space-between" spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 46,
            height: 46,
            borderRadius: 2,
            color: 'primary.main',
            bgcolor: 'primary.light',
          }}
        >
          <Icon size={24} strokeWidth={1.8} />
        </Box>
        {state && <Chip label={state} size="small" variant="outlined" />}
      </Stack>
      <Box>
        <Typography variant="h3" sx={{ mb: 0.75 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      {onClick && (
        <Box sx={{ color: 'primary.main', lineHeight: 0 }}>
          <ArrowRight size={17} />
        </Box>
      )}
    </Stack>
  </Paper>
);

const MainDashboard = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', width: '100%' }}>
      <PageHeader
        title="Welcome to Synapsys"
        description="Organize projects, patients, samples, and analyses from one workspace."
      />

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.25 },
          mb: 2.5,
          overflow: 'hidden',
          color: 'common.white',
          borderRadius: 3,
          background:
            'radial-gradient(circle at 86% 12%, rgba(159, 214, 255, 0.52), transparent 32%), linear-gradient(120deg, #0059B3, #0072E5)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h3" sx={{ color: 'inherit', mb: 0.5 }}>
              Create a project
            </Typography>
            <Typography
              variant="body2"
              sx={(theme) => ({
                color: alpha(theme.palette.common.white, 0.82),
              })}
            >
              Configure experiments, samples, and analysis pipelines.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => navigate('projects/create')}
            sx={{
              alignSelf: { xs: 'flex-start', sm: 'center' },
              color: 'primary.dark',
              bgcolor: 'common.white',
              '&:hover': { bgcolor: 'primary.light' },
            }}
          >
            Create project
          </Button>
        </Stack>
      </Paper>

      <Stack spacing={0.25} sx={{ mb: 1.5 }}>
        <Typography variant="h2">Workspaces</Typography>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={4}>
          <WorkspaceCard
            title="Projects"
            description="Configure study workspaces, experiments, and input samples."
            icon={FolderKanban}
            onClick={() => navigate('projects')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <WorkspaceCard
            title="Patients"
            description="Manage patient records and their linked clinical data."
            icon={Users}
            onClick={() => navigate('patients')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <WorkspaceCard
            title="Samples"
            description="Search and review samples before configuring an experiment."
            icon={Dna}
            onClick={() => navigate('patients')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <WorkspaceCard
            title="Analysis"
            description="Review available analysis workflows and downstream evidence."
            icon={Microscope}
            state="Available soon"
          />
        </Grid>
      </Grid>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ mt: 5, color: 'text.secondary' }}
      >
        <Box component="img" src={logo} alt="" sx={{ width: 28, height: 28 }} />
        <Typography variant="caption">
          Synapsys clinical genomics platform
        </Typography>
      </Stack>
    </Box>
  );
};

export default MainDashboard;
